import { cn } from '@/lib/utils';
import type { FlowStep } from '../flowTypes';
import { getStepIcon } from './stepIcons';

type StepConfig = {
  id: FlowStep;
  label: string;
};

export type StepProgressProps = {
  steps: StepConfig[];
  currentStep: FlowStep;
  onSelect?: (step: FlowStep) => void;
};

const StepProgress = ({ steps, currentStep, onSelect }: StepProgressProps) => {
  const currentIndex = Math.max(
    steps.findIndex((step) => step.id === currentStep),
    0,
  );

  return (
    <div
      className="flex items-center justify-between gap-4 rounded-2xl border border-muted-foreground/10 bg-muted/10 px-6 py-4"
      role="list"
    >
      {steps.map((step, index) => {
        const status =
          index < currentIndex ? 'completed' : index === currentIndex ? 'current' : 'upcoming';
        const clickable = index <= currentIndex && onSelect;
        const Icon = getStepIcon(step.id);

        const content = (
          <div
            className={cn(
              'flex h-12 w-12 items-center justify-center rounded-full border transition-colors',
              status === 'completed' &&
                'border-primary/40 bg-primary/10 text-primary shadow-[0_0_0_1px_rgba(145,122,255,0.25)]',
              status === 'current' &&
                'border-primary bg-primary text-primary-foreground shadow-[0_12px_30px_rgba(145,122,255,0.35)]',
              status === 'upcoming' && 'border-muted/30 bg-muted/10 text-muted-foreground',
            )}
            data-status={status}
          >
            <Icon aria-hidden="true" className="h-5 w-5" />
            <span className="sr-only">{step.label}</span>
          </div>
        );

        return (
          <div
            key={step.id}
            className="flex flex-1 items-center justify-center"
            role="listitem"
            aria-current={status === 'current' ? 'step' : undefined}
          >
            {clickable ? (
              <button
                type="button"
                onClick={() => onSelect?.(step.id)}
                className="rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
                aria-label={step.label}
              >
                {content}
              </button>
            ) : (
              <div aria-label={step.label}>{content}</div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default StepProgress;
